import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { VendorOrderFile } from '../../../../../../../shared/models/vendor-order.model';
import {
  TargetLanguageFileGroup,
  FileCancellationSelection,
  JobFileWithCancellation,
} from '../../../../../../../shared/models/file-cancellation.model';
import { FileGroupingService } from '../../../../../../../shared/services/file-grouping.service';

/**
 * Modal component for selecting specific files to cancel.
 * Groups files by target language and enforces type/upload eligibility rules.
 */
@Component({
  selector: 'app-file-selection-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatIconModule,
  ],
  templateUrl: './file-selection-modal.component.html',
  styleUrl: './file-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileSelectionModalComponent {
  // Inputs
  readonly jobUid = input.required<string>();
  readonly files = input.required<VendorOrderFile[]>();
  readonly targetLocales = input<Record<string, string>>({});

  // Outputs
  readonly fileSelectionChanged = output<FileCancellationSelection>();
  readonly continuedWithFiles = output<string[]>();
  readonly closed = output<void>();

  private readonly fileGroupingService: FileGroupingService = inject(FileGroupingService);

  readonly languageGroups = signal<TargetLanguageFileGroup[]>([]);
  readonly selectedFileUids = signal<Set<string>>(new Set());

  readonly totalCancellableFiles = computed(() =>
    this.fileGroupingService.getTotalCancellableCount(this.languageGroups()),
  );

  /** Count selected files directly from signals — no object mutations needed. */
  readonly totalSelectedFiles = computed(() => {
    const selected = this.selectedFileUids();
    return this.languageGroups()
      .flatMap((g) => g.files)
      .filter((f) => f.isCancellable && selected.has(f.fileUid)).length;
  });

  readonly isContinueDisabled = computed(() => this.totalSelectedFiles() === 0);

  constructor() {
    // Rebuild language groups whenever files input changes.
    // Must be in constructor so effect() runs in injection context.
    effect(() => {
      const groups = this.fileGroupingService.groupFilesByLanguage(
        this.files(),
        this.targetLocales(),
      );
      this.languageGroups.set(groups);
    });

    // Emit selection state whenever selection or groups change.
    effect(() => {
      const selected = this.selectedFileUids();
      this.fileSelectionChanged.emit({
        selectedFileUids: Array.from(selected),
        selectedByLanguage: this.buildSelectedByLanguage(selected),
        totalSelected: selected.size,
      });
    });
  }

  /** Count of selected cancellable files for a specific language group. */
  getGroupSelectedCount(group: TargetLanguageFileGroup): number {
    const selected = this.selectedFileUids();
    return group.files.filter((f: JobFileWithCancellation) => f.isCancellable && selected.has(f.fileUid))
      .length;
  }

  setGroupExpanded(group: TargetLanguageFileGroup, isExpanded: boolean): void {
    group.expanded = isExpanded;
    // Spread into a new array so the signal reference changes and computed signals re-evaluate.
    this.languageGroups.set([...this.languageGroups()]);
  }

  toggleFileSelection(fileUid: string, isCancellable: boolean): void {
    if (!isCancellable) {
      return;
    }
    const next = new Set(this.selectedFileUids());
    if (next.has(fileUid)) {
      next.delete(fileUid);
    } else {
      next.add(fileUid);
    }
    this.selectedFileUids.set(next);
  }

  onFileRowClick(file: JobFileWithCancellation): void {
    this.toggleFileSelection(file.fileUid, file.isCancellable);
  }

  isFileSelected(fileUid: string): boolean {
    return this.selectedFileUids().has(fileUid);
  }

  getFileRowClass(file: TargetLanguageFileGroup['files'][number]): string {
    if (file.isCancelled) return 'file-row file-row--cancelled';
    if (!file.isCancellable) return 'file-row file-row--disabled';
    if (this.isFileSelected(file.fileUid)) return 'file-row file-row--selected';
    return 'file-row';
  }

  getEligibleFiles(
    group: TargetLanguageFileGroup,
  ): JobFileWithCancellation[] {
    return group.files.filter((file) => file.isCancellable);
  }

  getCompletedTranslationFiles(
    group: TargetLanguageFileGroup,
  ): JobFileWithCancellation[] {
    return group.files.filter(
      (file) => !file.isCancellable && !this.isAlreadyCancelled(file),
    );
  }

  getAlreadyCancelledFiles(
    group: TargetLanguageFileGroup,
  ): JobFileWithCancellation[] {
    return group.files.filter((file) => this.isAlreadyCancelled(file));
  }

  private isAlreadyCancelled(file: JobFileWithCancellation): boolean {
    return file.isCancelled || file.status === 'CANCELLED';
  }

  onContinue(): void {
    const selected = Array.from(this.selectedFileUids());
    if (selected.length > 0) {
      this.continuedWithFiles.emit(selected);
    }
  }

  onClose(): void {
    this.closed.emit();
  }

  private buildSelectedByLanguage(
    selected: Set<string>,
  ): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    this.languageGroups().forEach((group) => {
      const groupSelected = group.files
        .filter((f: JobFileWithCancellation) => f.isCancellable && selected.has(f.fileUid))
        .map((f: JobFileWithCancellation) => f.fileUid);
      if (groupSelected.length > 0) {
        result[group.targetLanguageCode] = groupSelected;
      }
    });
    return result;
  }
}

